import { useQuery } from "@tanstack/react-query";
import { PROOFING_FLOW_APP_ID } from "@/config";
import {
  projectsBulkGetProjectsByIds,
  v2UsersAppProjects,
} from "@/usersAndProjects-client/sdk.gen";
import type { ProjectWithPackages } from "../utils/packageEnrichment";

/**
 * Fetch user's proofing projects with their assigned packages
 *
 * This hook fetches only the projects assigned to the user (via /api/users/{sub}/projects)
 * filtered by the proofing app, then performs one bulk project fetch. This reduces
 * API calls by only fetching projects the user has access to, instead of all tenant projects.
 */
export interface UseFetchProjectsWithPackagesResult {
  projectsWithPackages: ProjectWithPackages[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchProjectsWithPackages = (
  userSub: string | null | undefined,
  tenantSlug?: string,
  options?: { enabled?: boolean },
): UseFetchProjectsWithPackagesResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["projectsWithPackages", "user", userSub],
    queryFn: async () => {
      if (!userSub) {
        throw new Error("User sub is required");
      }

      // Step 1: Fetch user's projects filtered by proofing app (includes group projects)
      const projectsResponse = await v2UsersAppProjects({
        path: { sub: userSub, appId: PROOFING_FLOW_APP_ID },
      });
      const projects = projectsResponse.data || [];
      if (projects.length === 0) {
        return [];
      }

      const projectIds = projects.map((project) => project.id);
      const bulkResponse = await projectsBulkGetProjectsByIds({
        body: {
          projectIds,
        },
      });
      const bulkProjects = bulkResponse.data || [];

      const accessByProjectId = new Map(projects.map((project) => [project.id, project.accessVia]));

      const projectsWithPackages: ProjectWithPackages[] = bulkProjects.map((bulkProject) => {
        const accessVia = accessByProjectId.get(bulkProject.id);

        return {
          ...bulkProject,
          tenantSlug: tenantSlug || bulkProject.tenantSlug || "",
          packages: bulkProject.packages || [],
          assignedPackages: [],
          assignedUsers: [],
          ...(accessVia ? { ownedBy: accessVia === "direct" ? "user" : accessVia } : {}),
        };
      });

      return projectsWithPackages;
    },
    enabled: (options?.enabled ?? true) && !!userSub,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    projectsWithPackages: data || null,
    isLoading,
    error: error as Error | null,
  };
};
